import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from '../../i18n/LanguageContext';
import DatePicker from 'react-datepicker';
import { Download, Users, MapPin, Clock, BarChart3, FileText, Calendar, Filter, Home, Radio } from 'lucide-react';
import { ModernCard, ModernBadge, colorPalette } from '../ModernPageLayout';
import { API_BASE } from '../../config';
import "react-datepicker/dist/react-datepicker.css";

const ModernReportGenerator = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const { enabledFeatures = [], billingLoaded = false } = useOutletContext() || {};
  const location = useLocation();
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [reportType, setReportType] = useState('standard');
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [previewStats, setPreviewStats] = useState(null);

  // Types de rapports disponibles
  const reportTypes = useMemo(() => {
    const presenceTraceabilityEnabled = billingLoaded && enabledFeatures.includes('presence_traceability');
    const list = [
      {
        id: 'standard',
        name: t('reports.type_standard'),
        description: t('reports.type_standard_desc'),
        icon: <Clock className="w-5 h-5" />,
        color: 'bg-blue-500'
      },
      {
        id: 'gps',
        name: t('reports.type_gps'),
        description: t('reports.type_gps_desc'),
        icon: <MapPin className="w-5 h-5" />,
        color: 'bg-green-500'
      },
      {
        id: 'compliance',
        name: t('reports.type_compliance'),
        description: t('reports.type_compliance_desc'),
        icon: <BarChart3 className="w-5 h-5" />,
        color: 'bg-orange-500'
      },
      {
        id: 'detailed',
        name: t('reports.type_detailed'),
        description: t('reports.type_detailed_desc'),
        icon: <FileText className="w-5 h-5" />,
        color: 'bg-purple-500'
      }
    ];
    if (presenceTraceabilityEnabled) {
      list.push({
        id: 'presence_trace',
        name: t('reports.type_presence_trace'),
        description: t('reports.type_presence_trace_desc'),
        icon: <Radio className="w-5 h-5" />,
        color: 'bg-cyan-600'
      });
    }
    return list;
  }, [t, enabledFeatures, billingLoaded]);

  useEffect(() => {
    if (!reportTypes.some((type) => type.id === reportType)) {
      setReportType('standard');
    }
  }, [reportTypes, reportType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const requestedType = String(params.get('reportType') || '').trim();
    if (requestedType && reportTypes.some((type) => type.id === requestedType)) {
      setReportType(requestedType);
    }
  }, [location.search, reportTypes]);

  useEffect(() => {
    fetchEmployees();
    generatePreview();
  }, [startDate, endDate, selectedEmployee, reportType]); // preview + fetchEmployees when filters or reportType change

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // all=true, active=all, excludeSelf=true → saare employees minus logged-in user (admin/manager apna naam dropdown mein na aaye)
      const params = new URLSearchParams({ all: 'true', active: 'all', excludeSelf: 'true' });
      const response = await fetch(`${API_BASE}/api/employees?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Réponse non-JSON pour /api/employees:', text.substring(0, 200));
        throw new Error('Le serveur a retourné une réponse non-JSON');
      }
      
      const data = await response.json();
      if (data.success) {
        // Backend returns paginated: data.data = array, data.pagination = { total }
        const employeesList = data.data || data.employees || [];
        if (Array.isArray(employeesList)) {
          const employeesWithFullName = employeesList.map(emp => ({
            ...emp,
            fullName: `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim()
          }));
          setEmployees(employeesWithFullName);
        }
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    }
  };

  const getClientTimezone = () =>
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      : '';

  const getReportHeaders = () => {
    const token = localStorage.getItem('token');
    const tz = getClientTimezone();
    const headers = { 'Authorization': `Bearer ${token}` };
    if (tz) headers['X-User-Timezone'] = tz;
    headers['X-App-Language'] = language || 'fr';
    return headers;
  };

  const generatePreview = async () => {
    if (!startDate || !endDate) {
      setPreviewStats(null);
      return;
    }
    try {
      const tz = getClientTimezone();
      const params = new URLSearchParams({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        reportType: reportType,
        lang: language || 'fr',
        ...(tz && { timezone: tz }),
        ...(selectedEmployee && { employeeId: selectedEmployee })
      });

      const response = await fetch(`${API_BASE}/api/reports/preview?${params}`, {
        headers: getReportHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Réponse non-JSON pour /api/reports/preview:', text.substring(0, 200));
        throw new Error('Le serveur a retourné une réponse non-JSON');
      }
      
      const data = await response.json();
      setPreviewStats(data);
    } catch (error) {
      console.error('Erreur preview:', error);
      setPreviewStats(null);
    }
  };

  const generateReport = async (format) => {
    if (!startDate || !endDate) {
      setMessage(`❌ ${t('reports.error_select_period')}`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsGenerating(true);
    setMessage('');
    
    try {
      const tz = getClientTimezone();
      const params = new URLSearchParams({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        reportType: reportType,
        lang: language || 'fr',
        ...(tz && { timezone: tz }),
        ...(selectedEmployee && { employeeId: selectedEmployee })
      });

      const endpoint = format === 'pdf' ? 'generate-enhanced-pdf' : 'generate-enhanced-excel';
      const response = await fetch(`${API_BASE}/api/reports/${endpoint}?${params}`, {
        headers: getReportHeaders()
      });

      // Vérifier les quotas
      if (response.status === 429) {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.message || t('reports.error_quota_reached')}`);
        setTimeout(() => setMessage(''), 8000);
        return;
      }

      if (!response.ok) {
        throw new Error(t('reports.error_generation'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('reports.filename_prefix')}-${reportType}-${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      try {
        document.body.appendChild(a);
        a.click();
      } finally {
        try { if (a && a.parentNode) a.parentNode.removeChild(a); } catch (err) { console.warn('Could not remove temporary download link:', err); }
        try { window.URL.revokeObjectURL(url); } catch (e) {}
      }

      setMessage(`✅ ${t('reports.success_generated').replace('{{format}}', format.toUpperCase())}`);
      setTimeout(() => setMessage(''), 5000);
      
    } catch (error) {
      console.error('Erreur:', error);
      setMessage(`❌ ${t('reports.error_generation')}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedReportType = reportTypes.find(type => type.id === reportType);

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('reports.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <BarChart3 size={32} />
            {t('reports.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('reports.subtitle')}
        </p>
      </div>

      <div className="reports-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
      {/* Message */}
      {message && (
        <ModernCard
          gradient={message.includes('✅') 
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))'}
          style={{ marginBottom: '24px', textAlign: 'center' }}
        >
          <p style={{ 
            margin: 0, 
            fontSize: '16px', 
            fontWeight: 600,
            color: message.includes('✅') ? '#10b981' : '#ef4444'
          }}>
            {message}
          </p>
        </ModernCard>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Types de rapports */}
        <ModernCard
          title={t('reports.report_type_title')}
          icon={<Filter size={20} />}
          gradient={colorPalette.rapports.gradient}
          shadow={colorPalette.rapports.shadow}
          style={{ gridColumn: '1 / -1' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {reportTypes.map(type => (
              <div
                key={type.id}
                onClick={() => setReportType(type.id)}
                style={{
                  padding: '20px',
                  borderRadius: '16px',
                  border: reportType === type.id 
                    ? '2px solid rgba(255, 255, 255, 0.5)' 
                    : '2px solid rgba(255, 255, 255, 0.1)',
                  background: reportType === type.id
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: reportType === type.id ? 'scale(1.02)' : 'scale(1)',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  if (reportType !== type.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'scale(1.01)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (reportType !== type.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <div style={{
                    padding: '10px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {type.icon}
                  </div>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: 700, 
                    margin: 0, 
                    color: '#ffffff' 
                  }}>
                    {type.name}
                  </h4>
                </div>
                <p style={{ 
                  fontSize: '14px', 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  margin: 0 
                }}>
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </ModernCard>

        {/* Filtres */}
        <ModernCard
          title={t('reports.filters_title')}
          icon={<Calendar size={20} />}
          gradient={colorPalette.rapports.gradient}
          shadow={colorPalette.rapports.shadow}
          style={{ gridColumn: '1 / -1', overflow: 'visible' }}
        >
          {/* Période */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#ffffff', 
              marginBottom: '12px' 
            }}>
              {t('reports.period_label')}:
            </label>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              flexWrap: 'wrap' 
            }}>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText={t('reports.start_date_placeholder')}
                dateFormat="dd/MM/yyyy"
                portalId="reports-datepicker-portal"
                popperProps={{ strategy: 'fixed' }}
                popperClassName="reports-datepicker-popper"
                style={{
                  padding: '12px 16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                className="date-picker-modern"
              />
              <span style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: 'rgba(255, 255, 255, 0.8)' 
              }}>
                {t('reports.to')}
              </span>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={t('reports.end_date_placeholder')}
                dateFormat="dd/MM/yyyy"
                portalId="reports-datepicker-portal"
                popperProps={{ strategy: 'fixed' }}
                popperClassName="reports-datepicker-popper"
                style={{
                  padding: '12px 16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                className="date-picker-modern"
              />
            </div>
          </div>

          {/* Employé */}
          <div>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#ffffff', 
              marginBottom: '12px' 
            }}>
              <Users size={16} />
              {t('reports.employee_label')}:
            </label>
            <select 
              value={selectedEmployee} 
              onChange={(e) => setSelectedEmployee(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="" style={{ background: '#1e293b' }}>{t('reports.all_employees')}</option>
              {employees
                .filter(emp => emp?.fullName?.trim())
                .sort((a, b) => a.fullName.localeCompare(b.fullName))
                .map(emp => (
                  <option key={emp.id} value={emp.id} style={{ background: '#1e293b' }}>
                    {emp.fullName} ({emp.position || emp.department || t('reports.position_not_defined')})
                  </option>
                ))
              }
            </select>
            {employees.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <ModernBadge variant="success" icon="✅">
                  {t('reports.employees_available').replace('{{count}}', employees.filter(emp => emp.fullName?.trim()).length)}
                </ModernBadge>
              </div>
            )}
          </div>
        </ModernCard>
      </div>

      {/* Aperçu et Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px'
      }}>
        {/* Aperçu */}
        {previewStats && (
          <ModernCard
            title={t('reports.preview_title')}
            icon={<BarChart3 size={20} />}
            gradient={colorPalette.rapports.gradient}
            shadow={colorPalette.rapports.shadow}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_employees_hint')}>{t('reports.preview_employees')}:</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>
                  {previewStats.employeeCount ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_attendances_hint')}>{t('reports.preview_attendances')}:</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>
                  {previewStats.totalRecords ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_with_gps_hint')}>{t('reports.preview_with_gps')}:</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>
                  {previewStats.gpsRecords ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_outside_zone_hint')}>{t('reports.preview_outside_zone')}:</span>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#f59e0b' }}>
                  {previewStats.outsideZone ?? 0}
                </span>
              </div>
              {reportType === 'presence_trace' && previewStats.presenceEventCount != null && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_presence_events_hint')}>
                      {t('reports.preview_presence_events')}:
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#22d3ee' }}>
                      {previewStats.presenceEventCount ?? 0}
                    </span>
                  </div>
                  {previewStats.siteSummaryRows != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_site_summary_hint')}>
                        {t('reports.preview_site_summary_rows')}:
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#2dd4bf' }}>
                        {previewStats.siteSummaryRows}
                      </span>
                    </div>
                  )}
                  {previewStats.presenceHourlyPrompts != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }} title={t('reports.preview_presence_hourly_prompts_hint')}>
                        {t('reports.preview_presence_hourly_prompts')}:
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#fbbf24' }}>
                        {previewStats.presenceHourlyPrompts}
                      </span>
                    </div>
                  )}
                  {previewStats.presenceHourlyConfirmed != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>{t('reports.preview_presence_confirmed')}:</span>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#34d399' }}>
                        {previewStats.presenceHourlyConfirmed}
                      </span>
                    </div>
                  )}
                  {previewStats.presenceHourlyNoResponse != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>{t('reports.preview_presence_missed')}:</span>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#f87171' }}>
                        {previewStats.presenceHourlyNoResponse}
                      </span>
                    </div>
                  )}
                </>
              )}
              <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', margin: '8px 0 0', lineHeight: 1.4 }}>
                {t('reports.preview_footnote')}
              </p>
            </div>
          </ModernCard>
        )}

        {/* Type sélectionné */}
        {selectedReportType && (
          <ModernCard
            title={t('reports.selected_report_title')}
            gradient={colorPalette.rapports.gradient}
            shadow={colorPalette.rapports.shadow}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {selectedReportType.icon}
              </div>
              <div>
                <h4 style={{ 
                  fontSize: '16px', 
                  fontWeight: 700, 
                  margin: '0 0 4px 0', 
                  color: '#ffffff' 
                }}>
                  {selectedReportType.name}
                </h4>
                <p style={{ 
                  fontSize: '13px', 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  margin: 0 
                }}>
                  {selectedReportType.description}
                </p>
              </div>
            </div>
          </ModernCard>
        )}

        {/* Boutons de génération */}
        <ModernCard
          title={t('reports.generation_title')}
          icon={<Download size={20} />}
          gradient={colorPalette.rapports.gradient}
          shadow={colorPalette.rapports.shadow}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button 
              onClick={() => generateReport('pdf')}
              disabled={isGenerating}
              style={{
                width: '100%',
                padding: '16px 24px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 700,
                color: '#ffffff',
                background: isGenerating
                  ? 'rgba(107, 114, 128, 0.5)'
                  : 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: 'none',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: isGenerating ? 'none' : '0 4px 16px rgba(239, 68, 68, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.3)';
                }
              }}
            >
              {isGenerating ? t('reports.generating') : t('reports.generate_pdf')}
            </button>
            
            <button 
              onClick={() => generateReport('excel')}
              disabled={isGenerating}
              style={{
                width: '100%',
                padding: '16px 24px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 700,
                color: '#ffffff',
                background: isGenerating
                  ? 'rgba(107, 114, 128, 0.5)'
                  : 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: isGenerating ? 'none' : '0 4px 16px rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
                }
              }}
            >
              {isGenerating ? t('reports.generating') : t('reports.generate_excel')}
            </button>
          </div>
        </ModernCard>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (min-width: 768px) {
          .reports-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .reports-content {
            padding: 180px 60px 60px !important;
          }
        }
        .reports-content {
          animation: slideUp 0.4s ease-out;
        }
        .date-picker-modern {
          color: #ffffff !important;
        }
        .date-picker-modern::placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        .react-datepicker-wrapper {
          width: auto;
        }
        .react-datepicker__input-container input {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          border-radius: 12px !important;
          padding: 12px 16px !important;
          color: #ffffff !important;
          font-size: 14px !important;
          outline: none !important;
        }
        .react-datepicker__input-container input:focus {
          border-color: rgba(255, 255, 255, 0.5) !important;
          background: rgba(255, 255, 255, 0.15) !important;
        }
        .react-datepicker__input-container input::placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        #reports-datepicker-portal {
          position: relative;
          z-index: 10050;
        }
        .reports-datepicker-popper {
          z-index: 10050 !important;
        }
      `}</style>
      </div>
    </div>
  );
};

export default ModernReportGenerator;